/**
 * TranslationTermsSection — embedded panel on /compliance (#50 stage 2).
 *
 * The IAA-generator translation registry: (domain, stored value, language)
 * -> document term. Grouped by domain. Compliance/Admin get Add / Edit /
 * Delete (writes ride maker-checker, auto-applied for these roles); other
 * roles see a read-only table — same role-gating shape as the
 * completeness-rules sibling (non-editors get no write affordances).
 *
 * Identity is (domain, stored value, language); only the term and notes
 * are editable. The generator's fallback rule means an absent row renders
 * the stored value as-is — the explainer text says so, because "why is my
 * country printed in Portuguese?" is the predictable question.
 */
import {
  ActionIcon, Badge, Button, Card, Group, Modal, Select,
  Stack, Table, Text, TextInput, Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconLanguage, IconPencil, IconPlus, IconTrash } from '@tabler/icons-react';
import { useState } from 'react';

import { useCurrentUser } from '../hooks/useAuth';
import {
  useCreateTranslationTerm,
  useDeleteTranslationTerm,
  useTranslationTerms,
  useUpdateTranslationTerm,
} from '../hooks/useTranslationTerms';
import { useConfirmDelete } from '../hooks/useConfirmDelete';
import type { TranslationTerm, TranslationTermDomain } from '../api/types';
import { useAlertModal } from './AlertModalProvider';

const DOMAIN_LABELS: Record<TranslationTermDomain, string> = {
  CLASSIFICATION: 'Client classification (MiFID)',
  MARITAL_STATUS: 'Marital status',
  MARITAL_REGIME: 'Marital regime',
  ID_DOCUMENT_TYPE: 'ID document type',
  COUNTRY: 'Country names',
};

const DOMAIN_ORDER: TranslationTermDomain[] = [
  'CLASSIFICATION', 'MARITAL_STATUS', 'MARITAL_REGIME', 'ID_DOCUMENT_TYPE', 'COUNTRY',
];

const DOMAIN_OPTS = DOMAIN_ORDER.map((d) => ({ value: d, label: DOMAIN_LABELS[d] }));
const LANG_OPTS = [
  { value: 'PT', label: 'PT — Português' },
  { value: 'EN', label: 'EN — English' },
];

function AddTermModal({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  const [domain, setDomain] = useState<string | null>(null);
  const [dbValue, setDbValue] = useState('');
  const [language, setLanguage] = useState<string | null>(null);
  const [term, setTerm] = useState('');
  const [notes, setNotes] = useState('');
  const create = useCreateTranslationTerm();
  const { showAlert } = useAlertModal();

  const invalid = !domain || !language || dbValue.trim() === '' || term.trim() === '';

  function handleSave() {
    if (invalid) return; // defensive; button is disabled in this state
    create.mutate(
      {
        domain: domain as TranslationTermDomain,
        db_value: dbValue.trim(),
        language: language as 'PT' | 'EN',
        term: term.trim(),
        notes: notes.trim() || null,
      },
      {
        onSuccess: () => {
          notifications.show({
            color: 'green', icon: <IconCheck size={16} />,
            title: 'Term added',
            message: `${dbValue.trim()} → ${term.trim()} (${language})`,
          });
          onClose();
        },
        // Modal-stays-modal error pattern (#192 Edge case 6).
        onError: (e) => showAlert({
          color: 'red',
          title: 'Could not add the term',
          message: e.detail || 'Unknown error',
        }),
      },
    );
  }

  return (
    <Modal
      opened={opened} onClose={onClose} centered
      title={<Group gap="xs"><IconLanguage size={18} /><Text fw={600}>Add translation term</Text></Group>}
    >
      <Stack>
        <Select
          label="Domain" data={DOMAIN_OPTS} value={domain} onChange={setDomain}
          placeholder="What kind of value is this?" required
        />
        <TextInput
          label="Stored value"
          description="Exactly as it appears in the database / on the record (e.g. 'Casado', 'Espanha', 'NP')."
          value={dbValue} onChange={(e) => setDbValue(e.currentTarget.value)} required
        />
        <Select
          label="Document language" data={LANG_OPTS} value={language} onChange={setLanguage}
          placeholder="The language of the generated document" required
        />
        <TextInput
          label="Document term"
          description="What the generated document will print for this value."
          value={term} onChange={(e) => setTerm(e.currentTarget.value)} required
        />
        <TextInput
          label="Notes"
          description="Optional context for other compliance staff."
          value={notes} onChange={(e) => setNotes(e.currentTarget.value)}
        />
        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} loading={create.isPending} disabled={invalid}>
            Add term
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function EditTermModal({
  row, opened, onClose,
}: { row: TranslationTerm; opened: boolean; onClose: () => void }) {
  const [term, setTerm] = useState(row.term);
  const [notes, setNotes] = useState(row.notes ?? '');
  const update = useUpdateTranslationTerm(row.id);
  const { showAlert } = useAlertModal();
  const invalid = term.trim() === '';

  function handleSave() {
    if (invalid) return;
    update.mutate(
      { term: term.trim(), notes: notes.trim() || null },
      {
        onSuccess: () => {
          notifications.show({
            color: 'green', icon: <IconCheck size={16} />,
            title: 'Term updated',
            message: `${row.db_value} → ${term.trim()} (${row.language})`,
          });
          onClose();
        },
        onError: (e) => showAlert({
          color: 'red',
          title: 'Could not update the term',
          message: e.detail || 'Unknown error',
        }),
      },
    );
  }

  return (
    <Modal
      opened={opened} onClose={onClose} centered
      title={<Group gap="xs"><IconLanguage size={18} /><Text fw={600}>Edit translation term</Text></Group>}
    >
      <Stack>
        {/* Identity is fixed — shown as read-only badges, like the
            completeness-rules edit modal. */}
        <Group gap="xs">
          <Badge variant="light" color="azul">{DOMAIN_LABELS[row.domain]}</Badge>
          <Badge variant="light" color="petroleo">{row.db_value}</Badge>
          <Badge variant="light">{row.language}</Badge>
        </Group>
        <TextInput
          label="Document term"
          description="What the generated document will print for this value."
          value={term} onChange={(e) => setTerm(e.currentTarget.value)} required
        />
        <TextInput
          label="Notes"
          value={notes} onChange={(e) => setNotes(e.currentTarget.value)}
        />
        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} loading={update.isPending} disabled={invalid}>
            Save changes
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function TermRow({ row, canEdit }: { row: TranslationTerm; canEdit: boolean }) {
  const [opened, { open, close }] = useDisclosure(false);
  const del = useDeleteTranslationTerm();
  const confirmDelete = useConfirmDelete();
  const { showAlert } = useAlertModal();

  return (
    <>
      <Table.Tr>
        <Table.Td><Text size="sm" fw={500}>{row.db_value}</Text></Table.Td>
        <Table.Td style={{ width: 60 }}><Badge variant="light">{row.language}</Badge></Table.Td>
        <Table.Td><Text size="sm">{row.term}</Text></Table.Td>
        <Table.Td><Text size="xs" c="dimmed">{row.notes ?? '—'}</Text></Table.Td>
        <Table.Td style={{ width: 90 }}>
          {canEdit && (
            <Group gap={4} wrap="nowrap">
              <ActionIcon variant="subtle" onClick={open} title="Edit term">
                <IconPencil size={16} />
              </ActionIcon>
              <ActionIcon
                variant="subtle" color="red" title="Delete term"
                loading={del.isPending}
                onClick={() => confirmDelete('translation term', () =>
                  del.mutate(row.id, {
                    onError: (e) => showAlert({
                      color: 'red',
                      title: 'Could not delete the term',
                      message: e.detail || 'Unknown error',
                    }),
                  }),
                )}
              >
                <IconTrash size={16} />
              </ActionIcon>
            </Group>
          )}
        </Table.Td>
      </Table.Tr>
      {canEdit && opened && <EditTermModal row={row} opened={opened} onClose={close} />}
    </>
  );
}

export function TranslationTermsSection() {
  const { user } = useCurrentUser();
  const canEdit = user?.role === 'admin' || user?.role === 'compliance';
  const termsQ = useTranslationTerms();
  const [addOpened, { open: openAdd, close: closeAdd }] = useDisclosure(false);

  if (termsQ.isLoading) {
    return (
      <Card withBorder p="md" radius="md">
        <Text size="sm" c="dimmed">Loading translation terms…</Text>
      </Card>
    );
  }
  if (termsQ.error) {
    return (
      <Card withBorder p="md" radius="md">
        <Text size="sm" c="red">Could not load translation terms: {termsQ.error.detail}</Text>
      </Card>
    );
  }

  const terms = termsQ.data?.items ?? [];
  const byDomain = new Map<TranslationTermDomain, TranslationTerm[]>();
  for (const t of terms) {
    const arr = byDomain.get(t.domain) ?? [];
    arr.push(t);
    byDomain.set(t.domain, arr);
  }

  return (
    <Card withBorder p="lg" radius="md">
      <Stack>
        <Group justify="space-between" align="center">
          <Group gap="xs" align="center">
            <IconLanguage size={20} />
            <Title order={4}>Translation registry</Title>
          </Group>
          {canEdit && (
            <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openAdd}>
              Add term
            </Button>
          )}
        </Group>
        <Text size="sm" c="dimmed">
          The terms generated documents (IAA drafts) print for stored values —
          e.g. classification <strong>NP</strong> prints as{' '}
          <strong>Investidor Não Profissional</strong> (PT) / <strong>Retail Client</strong> (EN).
          A value with no entry here is printed exactly as stored. Every export
          records the terms it used. {canEdit
            ? 'Changes apply to future exports only.'
            : 'Read-only — only Admin and Compliance can edit.'}
        </Text>

        {DOMAIN_ORDER.filter((d) => byDomain.has(d)).map((d) => (
          <Stack key={d} gap="xs">
            <Text size="sm" fw={600}>{DOMAIN_LABELS[d]}</Text>
            <Table withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Stored value</Table.Th>
                  <Table.Th style={{ width: 60 }}>Lang</Table.Th>
                  <Table.Th>Document term</Table.Th>
                  <Table.Th>Notes</Table.Th>
                  <Table.Th style={{ width: 90 }} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {byDomain.get(d)!
                  .slice()
                  .sort((a, b) => a.db_value.localeCompare(b.db_value) || a.language.localeCompare(b.language))
                  .map((t) => <TermRow key={t.id} row={t} canEdit={canEdit} />)}
              </Table.Tbody>
            </Table>
          </Stack>
        ))}

        {terms.length === 0 && (
          <Text size="sm" c="dimmed">
            No terms registered yet{canEdit ? ' — use "Add term" to create the first one' : ''}.
          </Text>
        )}
      </Stack>
      {canEdit && addOpened && <AddTermModal opened={addOpened} onClose={closeAdd} />}
    </Card>
  );
}
